/* eslint-disable react-hooks/exhaustive-deps */
import {
  BLOCK_TYPES,
  MODULES_ITEMS,
  QUESTIONS_TYPES,
  REVIEW_STATUS,
} from 'utils/constants';
import styles from 'styles/components/cycleModuleRender.module.scss';
import {
  Avatar,
  Button,
  Divider,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Spin,
} from 'antd';
import {
  CheckCircleFilled,
  EyeOutlined,
  FileTextOutlined,
  LoadingOutlined,
  SendOutlined,
  UserOutlined,
} from '@ant-design/icons';
import debounce from 'debounce';
import { useCallback, useEffect, useState } from 'react';
import {
  getMessageVisibilityFromType,
  getQuestionInputFromType,
  injectQuestionNumbersToModule,
} from 'utils/utils';
import dayjs from 'dayjs';
import AiSuggestionAnswer from './aiSuggestionAnswer';
import AnimatedAIButton from './animatedAIButton';

const CycleModuleRender = ({
  cycle,
  hideTitle = false,
  module: injectedModule,
  myCycleId,
  onSaveFullReview,
  task = {},
  form = null,
  review = null,
  lastSavedDate = null,
  saveLoading,
  submitLoading,
  reviewLoading,
  submit,
  onChange,
  isReadOnly = false,
  hideIfEmpty = false,
}) => {
  const [module, setModule] = useState(injectedModule);
  const [moduleExtraExpanded, setModuleExtraExpanded] = useState([]);
  useEffect(() => {
    if (injectedModule) {
      const moduleWithQuestionNumber =
        injectQuestionNumbersToModule(injectedModule);
      setModule(moduleWithQuestionNumber);
    }
  }, [injectedModule]);

  useEffect(() => {
    form?.resetFields();
  }, [review?._id]);

  const onChangeAnswer = (questionId, answerT) => onChange(questionId, answerT);
  const onSaveReview = (values) => {
    onSaveFullReview(values);
  };

  const debounceOnChange = useCallback(debounce(onChangeAnswer, 300), [
    onChange,
  ]);

  const debounceSaveReview = useCallback(debounce(onSaveReview, 300), [
    onChange,
  ]);

  const getDisplayFromBlock = (block, blockIndex, review) => {
    const shouldDisplayAiSuggestion =
      !reviewLoading &&
      !review.doneAt &&
      task.status !== REVIEW_STATUS.DONE &&
      task.status !== REVIEW_STATUS.READY &&
      !isReadOnly &&
      block?.params?.type === 'OPEN_ENDED' &&
      review?.answers?.find((a) => a.questionId === block._id)?.text.length > 0;

    const shouldDisplayCommentTextArea =
      !review.doneAt &&
      task.status !== REVIEW_STATUS.DONE &&
      task.status !== REVIEW_STATUS.READY &&
      !isReadOnly &&
      (block?.params?.type === QUESTIONS_TYPES.RATING.id ||
        block?.params?.type === QUESTIONS_TYPES.MULTIPLE_CHOICE.id) &&
      form?.getFieldValue(['answers', block._id]) &&
      block?.params?.hasComment;

    const shouldDisplayCommentReadonly =
      !reviewLoading &&
      (isReadOnly ||
        task.status === REVIEW_STATUS.DONE ||
        task.status === REVIEW_STATUS.READY || review.doneAt) &&
      (block?.params?.type === QUESTIONS_TYPES.RATING.id ||
        block?.params?.type === QUESTIONS_TYPES.MULTIPLE_CHOICE.id) &&
      review?.answers?.find((a) => a.questionId === block._id) &&
      block?.params?.hasComment;

    const isCommentOptional = block?.params?.isCommentOptional;

    switch (block.type) {
      default:
      case BLOCK_TYPES.HEADER.id:
        return <h2 key={`${task?.user?._id}_${block._id}`}>{block.text}</h2>;
      case BLOCK_TYPES.DIVIDER.id:
        return <Divider key={`${task?.user?._id}_${block._id}`} />;
      case BLOCK_TYPES.SECTION.id:
        return <div key={`${task?.user?._id}_${block._id}`}>{block.text}</div>;
      case BLOCK_TYPES.QUESTION.id:
        return isReadOnly ? (
          <div
            key={`${task?.user?._id}_${block._id}_${blockIndex}`}
            style={{ marginBottom: 24 }}
          >
            <div className={styles.questionText}>
              {`${block.questionNumber ? `${block.questionNumber}. ` : ''}${block.params.text
                }`}
            </div>
            <div
              dangerouslySetInnerHTML={{
                __html: review?.answers
                  ?.find((answer) => answer.questionId === block._id)
                  ?.text.replace(/\n/g, '<br />'),
              }}
            ></div>
            {shouldDisplayCommentReadonly && (
              <div style={{ marginTop: 6 }}>
                <div style={{ fontWeight: 'bold' }}>Comment</div>
                {review?.answers?.find((a) => a.questionId === block._id)
                  ?.comment || 'No comment provided'}
              </div>
            )}
          </div>
        ) : (
          <Form.Item
            key={`${task?.user?._id}_${block._id}_${blockIndex}`}
            className={styles.question}
            name={['answers', block._id]}
            label={
              <div className={styles.questionText}>
                {`${block.questionNumber ? `${block.questionNumber}. ` : ''}${block.params.text
                  }`}
              </div>
            }
            rules={[
              {
                required: block?.params?.isRequired,
                message: 'This field is required',
              },
            ]}
            extra={
              <>
                {shouldDisplayCommentTextArea && (
                  <div>
                    <div>
                      Comment{' '}
                      <span style={{ color: 'red' }}>
                        {isCommentOptional ? '' : '*'}
                      </span>
                    </div>
                    <Input.TextArea
                      autoSize={true}
                      key={`Comment_${task?.user?._id}_${block._id}_${blockIndex}`}
                      onChange={(e) => {
                        debounceOnChange(block._id, {
                          comment: e.target.value,
                          text: form?.getFieldValue(['answers', block._id]),
                        });
                      }}
                      defaultValue={
                        review?.answers?.find((a) => a.questionId === block._id)
                          ?.comment
                      }
                      required={!isCommentOptional}
                      placeholder={
                        isCommentOptional
                          ? 'Comment your answer?'
                          : 'Please comment your answer (required)'
                      }
                    />
                  </div>
                )}
                {shouldDisplayCommentReadonly && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ fontWeight: 'bold' }}>Comment</div>
                    {review?.answers?.find((a) => a.questionId === block._id)
                      ?.comment || 'No comment provided'}
                  </div>
                )}
                {shouldDisplayAiSuggestion &&
                  (moduleExtraExpanded.includes(block._id) ? (
                    <AiSuggestionAnswer
                      block={block}
                      form={form}
                      cycleId={myCycleId}
                      module={module}
                      answer={form?.getFieldValue(['answers', block._id])}
                      key={`${task?.user?._id}_${block._id}_${blockIndex}`}
                      isExpanded={moduleExtraExpanded.includes(block._id)}
                    />
                  ) : (
                    <AnimatedAIButton
                      disabled={isReadOnly || !form}
                      className={styles.aiSuggestionButton}
                      onClick={() => {
                        if (
                          !form?.getFieldValue(['answers', block._id]) ||
                          form?.getFieldValue(['answers', block._id])
                            ?.length === 0
                        ) {
                          message.error(
                            'Please enter an answer before improving with AI'
                          );
                          return;
                        }
                        setModuleExtraExpanded((prev) => [...prev, block._id]);
                      }}
                    >
                      Get AI feedback
                    </AnimatedAIButton>
                  ))}
              </>
            }
          >
            {getQuestionInputFromType(
              block,
              (questionId, value) => { },
              {
                disabled:
                  isReadOnly ||
                  reviewLoading ||
                  submitLoading ||
                  task.status === REVIEW_STATUS.DONE ||
                  task.status === REVIEW_STATUS.READY ||
                  !form ||
                  !myCycleId || review.doneAt,
              },
              isReadOnly,
              shouldDisplayAiSuggestion &&
                moduleExtraExpanded.includes(block._id)
                ? {
                  borderBottom: 0,
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                }
                : {}
            )}
          </Form.Item>
        );
    }
  };

  if (!task) {
    return (
      <div style={{ padding: '20px', color: 'grey', fontStyle: 'italic' }}>
        Select a review
      </div>
    );
  }
  return (
    <div className={styles.contentWrapper}>
      {!hideTitle && module && (
        <h2>
          <FileTextOutlined />
          &nbsp;{MODULES_ITEMS[module.type].label}
        </h2>
      )}
      {!hideTitle && task?.user && (
        <div className={styles.userInfo}>
          <div className={styles.userInfoLeft}>
            <div className={styles.userInfoAvatar}>
              <Avatar
                size={80}
                icon={<UserOutlined />}
                src={task.user.profile.profilePictureUrl}
              />
            </div>
            <div>
              <div className={styles.userInfoName}>
                {`${task.user.profile.firstName} ${task.user.profile.lastName}`}
              </div>
              <div className={styles.userInfoJobTitle}>
                {task.user.profile.jobTitle}
              </div>
            </div>
          </div>
        </div>
      )}
      {hideIfEmpty && !review?.answers ? (
        <Empty
          description="Review not submitted yet"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <Form
          form={form}
          className={styles.form}
          layout="vertical"
          onFieldsChange={(changedValues, allValues) => {
            if (!reviewLoading) {
              debounceSaveReview(allValues);
            }
          }}
          onFinish={() => {
            if (saveLoading) {
              message.info('Cycle is currently saving, please try again.');
              return;
            }
            Modal.confirm({
              icon: <SendOutlined style={{ color: '#1677ff' }} />,
              title: 'Submit review',
              content: 'Are you sure you want to submit the review?',
              onOk: submit,
              okText: 'Yes, Submit',
              cancelText: 'No, Keep editing',
              okButtonProps: {
                loading: submitLoading,
                icon: <SendOutlined />,
              },
            });
          }}
          initialValues={{
            answers: review?.answers?.reduce((acc, answer) => {
              acc[answer.questionId] = answer.text;
              return acc;
            }, {}),
          }}
        >
          <div
            className={styles.questions}
            {...(hideTitle ? { style: { marginTop: 0 } } : {})}
          >
            {module.blocks.map((block, blockIndex) =>
              getDisplayFromBlock(block, blockIndex, review)
            )}
          </div>

          <div className={styles.actions}>
            {review?.doneAt ? (
              <div className={styles.lastSavedDate}>
                <CheckCircleFilled /> Review submitted on{' '}
                {dayjs(review.doneAt).format('DD/MM/YYYY HH:mm')}
              </div>
            ) : (
              lastSavedDate && (
                <div className={styles.lastSavedDate}>
                  <CheckCircleFilled /> Last saved on{' '}
                  {dayjs(lastSavedDate).format('DD/MM/YYYY HH:mm')}
                </div>
              )
            )}
            {saveLoading && (
              <div className={styles.saveLoading}>
                <Spin indicator={<LoadingOutlined spin />} />
              </div>
            )}
            {form && myCycleId && !isReadOnly && (
              <Form.Item style={{ margin: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  className={styles.submitButton}
                  icon={<SendOutlined />}
                  style={{
                    width: 200,
                  }}
                  loading={submitLoading}
                  disabled={
                    review?.doneAt ||
                    task.status === REVIEW_STATUS.DONE ||
                    task.status === REVIEW_STATUS.READY
                  }
                >
                  Submit review
                </Button>
              </Form.Item>
            )}
          </div>
          {form &&
            myCycleId &&
            !isReadOnly &&
            !(
              review?.doneAt ||
              task.status === REVIEW_STATUS.DONE ||
              task.status === REVIEW_STATUS.READY
            ) && (
              <div style={{ textAlign: 'right', color: 'grey', marginTop: 12 }}>
                <EyeOutlined />{' '}
                {getMessageVisibilityFromType(module, cycle?.reviewVisibility)}
              </div>
            )}
        </Form>
      )}
    </div>
  );
};

export default CycleModuleRender;
